import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useAbrirSemana,
  useActualizarSemana,
  useCierrePreview,
  useEjecutarCorteA,
  useFijarLineaBase,
  useFotoB,
  useRevertirAvance,
  useRevertirAvancePreview,
  useSemanaActual,
} from '@/hooks/useCierre';
import { useSemanas } from '@/hooks/useReportes';
// Cierre/semana = gestión operativa (admin + supervisor). El nombre local
// `esAdmin` se mantiene por brevedad pero refleja "puede gestionar".
import { ErrorHttp, esGestion as esAdminFn, esAdmin as esAdminReal } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import type {
  CierrePreview,
  PreviewObra,
  ResultadoCorteA,
  SemanaOperativa,
} from '@adelante/shared';
import {
  ArrowCircleRight,
  CalendarPlus,
  Camera,
  CheckCircle,
  CircleNotch,
  ClockCounterClockwise,
  Flag,
  LockSimple,
  MagnifyingGlass,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AsignarNCCierreDialog } from './AsignarNCCierreDialog';

/**
 * Pantalla del Cierre semanal (Fase 4a UI). Una sola pantalla, dos roles:
 *   - Admin y supervisor de campo pueden cerrar la semana abierta.
 *   - Solo el admin puede abrir una semana nueva.
 *
 * Estados que maneja:
 *   - cargando
 *   - sin semana abierta → form de "Abrir semana" (admin) o aviso (campo)
 *   - con semana abierta → preview + "Cerrar semana"
 *   - cierre ejecutado → resultado + opción de abrir la siguiente (admin)
 */
type CategoriaCierre =
  | 'avanzaran'
  | 'avanzo_semana'
  | 'finalizaran'
  | 'no_completada'
  | 'sin_criticas';

/** Clasifica una obra del preview en una categoría (excluyente). */
function categoriaObra(o: PreviewObra): CategoriaCierre {
  if (o.criticas_total === 0) return 'sin_criticas';
  if (o.va_a_avanzar && o.sprint_siguiente === null) return 'finalizaran';
  if (o.va_a_avanzar) return 'avanzaran';
  if (o.avanzo_semana) return 'avanzo_semana';
  return 'no_completada';
}

const CATEGORIA_META: Record<CategoriaCierre, { label: string; chip: string }> = {
  avanzaran: {
    label: 'Avanzarán',
    chip: 'border-adelante-primary bg-adelante-primary/20 text-adelante-primary-dark',
  },
  avanzo_semana: {
    label: 'Avanzó semana',
    chip: 'border-indigo-300 bg-indigo-100 text-indigo-700',
  },
  finalizaran: { label: 'Finalizarán', chip: 'border-sky-300 bg-sky-100 text-sky-700' },
  no_completada: { label: 'No completadas', chip: 'border-red-300 bg-red-100 text-red-700' },
  sin_criticas: { label: 'Sin críticas', chip: 'border-zinc-300 bg-zinc-100 text-zinc-600' },
};

export function CierrePantalla() {
  const esAdmin = esAdminFn();
  const semanaQ = useSemanaActual();
  const previewQ = useCierrePreview();
  const ejecutar = useEjecutarCorteA();
  const [resultado, setResultado] = useState<ResultadoCorteA | null>(null);

  const semana = semanaQ.data?.data ?? null;
  const preview = previewQ.data?.data ?? null;

  if (semanaQ.isLoading || (semana && previewQ.isLoading)) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Cargando…
      </div>
    );
  }

  if (resultado) {
    return (
      <ResultadoVista resultado={resultado} esAdmin={esAdmin} onSeguir={() => setResultado(null)} />
    );
  }

  if (!semana) {
    return esAdmin ? <AbrirSemanaForm /> : <SinSemanaCampo />;
  }

  // Hay semana abierta — preview + cerrar.
  return (
    <CierreActivo
      semana={semana}
      preview={preview}
      enviando={ejecutar.isPending}
      onCerrar={async (nc, fechaCorte) => {
        try {
          const r = await ejecutar.mutateAsync({ nc, fecha_corte: fechaCorte });
          if (r.data) {
            setResultado(r.data);
            toast.success(`Semana cerrada (${r.data.snapshots_creados} snapshots).`);
          }
        } catch (e) {
          toast.error('No se pudo cerrar la semana', {
            description: e instanceof Error ? e.message : 'Error desconocido',
          });
        }
      }}
    />
  );
}

// ===========================================================================
// Sin semana — formulario admin para abrir, o aviso para campo.
// ===========================================================================

function SinSemanaCampo() {
  return (
    <Card className="flex flex-col items-center gap-3 p-6 text-center">
      <WarningCircle className="h-9 w-9 text-amber-500" weight="fill" aria-hidden="true" />
      <div>
        <p className="text-base font-semibold">No hay semana operativa abierta</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Pedile a un administrador que abra la semana desde el panel de admin para poder cerrarla.
        </p>
      </div>
    </Card>
  );
}

/**
 * Semana operativa = JUEVES → MIÉRCOLES siguiente (7 días). El Corte A cae el
 * miércoles (calza con los cortes de planilla/gastos). Toma el próximo jueves
 * en/después de `base` como inicio. Nº de semana ISO usando el jueves.
 */
function semanaJueMie(base: Date) {
  const d = new Date(Date.UTC(base.getFullYear(), base.getMonth(), base.getDate()));
  const haciaJueves = (4 - d.getUTCDay() + 7) % 7; // días hasta el próximo jueves (0 si ya es jueves)
  const jueves = new Date(d);
  jueves.setUTCDate(d.getUTCDate() + haciaJueves);
  const miercoles = new Date(jueves);
  miercoles.setUTCDate(jueves.getUTCDate() + 6);
  const yearStart = new Date(Date.UTC(jueves.getUTCFullYear(), 0, 1));
  const numero = Math.ceil(((jueves.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  const iso = (x: Date) => x.toISOString().slice(0, 10);
  return {
    anio: jueves.getUTCFullYear(),
    numero,
    fecha_inicio: iso(jueves),
    fecha_fin: iso(miercoles),
  };
}

function AbrirSemanaForm({ defaultDate }: { defaultDate?: Date } = {}) {
  const semanasQ = useSemanas();
  const semanas = semanasQ.data?.data ?? [];
  // Fecha base de la semana NUEVA = el día siguiente al FIN de la última semana
  // registrada (encadena jueves→miércoles sin repetir ni saltar el rango). Así
  // tras cerrar la 24 (…→10/06), la 25 arranca el jueves 11/06. Si no hay
  // semanas previas, se basa en hoy (o en defaultDate).
  const sugerida = useMemo(() => {
    const fines = semanas
      .map((s) => s.fecha_fin?.slice(0, 10))
      .filter((f): f is string => !!f)
      .sort();
    const ultima = fines.at(-1);
    if (ultima) {
      const base = new Date(`${ultima}T00:00:00Z`);
      base.setUTCDate(base.getUTCDate() + 1); // día siguiente al miércoles de cierre = jueves
      return semanaJueMie(base);
    }
    return semanaJueMie(defaultDate ?? new Date());
  }, [semanas, defaultDate]);
  // Nº de semana = SIGUIENTE en la secuencia operativa (último del año + 1), no
  // el ISO — para no chocar con semanas ya existentes (UQ anio+numero, da 409).
  const numSiguiente = useMemo(() => {
    const delAnio = semanas.filter((s) => s.anio === sugerida.anio);
    return delAnio.length > 0
      ? Math.max(...delAnio.map((s) => s.numero_semana)) + 1
      : sugerida.numero;
  }, [semanas, sugerida]);
  const [anio, setAnio] = useState(String(sugerida.anio));
  const [num, setNum] = useState(String(sugerida.numero));
  const [fIni, setFIni] = useState(sugerida.fecha_inicio);
  const [fFin, setFFin] = useState(sugerida.fecha_fin);
  const [dias, setDias] = useState('5');
  // La semana operativa es SIEMPRE jueves→miércoles: fechas bloqueadas por
  // defecto, con un check para ajustar manualmente (feriados, semana corta…).
  const [editable, setEditable] = useState(false);
  // Al cargar las semanas existentes, sincroniza nº + fechas sugeridas (salvo
  // edición manual) — encadenando la nueva semana tras la última registrada.
  useEffect(() => {
    if (editable) return;
    setNum(String(numSiguiente));
    setAnio(String(sugerida.anio));
    setFIni(sugerida.fecha_inicio);
    setFFin(sugerida.fecha_fin);
  }, [numSiguiente, sugerida, editable]);
  const abrir = useAbrirSemana();

  async function handleAbrir() {
    try {
      await abrir.mutateAsync({
        anio: Number(anio),
        numero_semana: Number(num),
        fecha_inicio: fIni,
        fecha_fin: fFin,
        dias_efectivos: Number(dias) || 5,
      });
      toast.success(`Semana ${anio}-S${num} abierta`);
    } catch (e) {
      toast.error('No se pudo abrir', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  return (
    <Card className="space-y-4 p-5">
      <div className="flex items-center gap-2">
        <CalendarPlus
          className="h-5 w-5 text-adelante-primary-dark"
          weight="bold"
          aria-hidden="true"
        />
        <h2 className="text-lg font-semibold">Abrir semana operativa</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        No hay semana abierta. La semana operativa va de <strong>jueves a miércoles</strong> (el
        cierre cae el miércoles). Toda la captura de avance cae en esta semana hasta que la cierres.
      </p>
      <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <input
          type="checkbox"
          checked={editable}
          onChange={(e) => setEditable(e.target.checked)}
          className="h-4 w-4 accent-adelante-primary"
        />
        Editar fechas manualmente (por defecto: jueves → miércoles)
      </label>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="sem-anio" className="mb-1 block text-xs">
            Año
          </Label>
          <Input
            id="sem-anio"
            type="number"
            value={anio}
            disabled={!editable}
            onChange={(e) => setAnio(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="sem-num" className="mb-1 block text-xs">
            N° de semana
          </Label>
          <Input
            id="sem-num"
            type="number"
            value={num}
            disabled={!editable}
            onChange={(e) => setNum(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="sem-ini" className="mb-1 block text-xs">
            Inicio (jueves)
          </Label>
          <Input
            id="sem-ini"
            type="date"
            value={fIni}
            disabled={!editable}
            onChange={(e) => setFIni(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="sem-fin" className="mb-1 block text-xs">
            Fin (miércoles)
          </Label>
          <Input
            id="sem-fin"
            type="date"
            value={fFin}
            disabled={!editable}
            onChange={(e) => setFFin(e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="sem-dias" className="mb-1 block text-xs">
            Días efectivos (descontá feriados)
          </Label>
          <Input
            id="sem-dias"
            type="number"
            min={1}
            max={7}
            value={dias}
            onChange={(e) => setDias(e.target.value)}
          />
        </div>
      </div>
      <Button onClick={handleAbrir} disabled={abrir.isPending} className="w-full">
        {abrir.isPending ? (
          <>
            <CircleNotch className="mr-2 h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
            Abriendo…
          </>
        ) : (
          'Abrir semana'
        )}
      </Button>
    </Card>
  );
}

// ===========================================================================
// Con semana abierta — preview + cerrar.
// ===========================================================================

function CierreActivo({
  semana,
  preview,
  enviando,
  onCerrar,
}: {
  semana: SemanaOperativa;
  preview: CierrePreview | null;
  enviando: boolean;
  onCerrar: (nc: Record<string, string>, fechaCorte: string) => void;
}) {
  const data = preview;
  const [confirmar, setConfirmar] = useState(false);
  const [revertirAbierto, setRevertirAbierto] = useState(false);
  const [proyecto, setProyecto] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<CategoriaCierre | null>(null);
  // NC asignadas en el modal: "obra|sub" → código de causa.
  const [nc, setNc] = useState<Record<string, string>>({});
  const [modalNC, setModalNC] = useState(false);
  const [fotoBAbierto, setFotoBAbierto] = useState(false);
  const [fechaFotoB, setFechaFotoB] = useState(() => new Date().toISOString().slice(0, 10));
  // Fecha de corte del Cierre — default MIÉRCOLES de cierre de la semana
  // operativa (fecha_fin = último día). La semana es jueves→miércoles, así que
  // el corte cae el miércoles, que calza con los cortes de planilla/gastos.
  // Va CONGELADA por defecto: solo gerencia la descongela si necesita cambiarla.
  const [fechaCorteA, setFechaCorteA] = useState(() => semana.fecha_fin.slice(0, 10));
  const [corteEditable, setCorteEditable] = useState(false);
  const esAdmin = esAdminFn();
  const esAdminEstricto = esAdminReal();

  // Pendientes que requieren NC para el Corte A. Solo las obras que NO avanzan
  // (críticas incompletas) necesitan NC; las que avanzan/finalizan o no tienen
  // críticas, no. Sobre TODAS las obras (el cierre las procesa todas).
  const pendientesSinNC = useMemo(() => {
    let n = 0;
    for (const o of data?.obras ?? []) {
      // Congeladas (en_espera) no se cierran ni requieren NC → no cuentan.
      if (o.estado === 'en_espera') continue;
      if (o.va_a_avanzar || o.avanzo_semana || o.criticas_total === 0) continue;
      for (const s of o.subs) {
        if (s.completada) continue;
        if (!s.nc_causa && !nc[`${o.codigo}|${s.sub_partida_id}`]) n++;
      }
    }
    return n;
  }, [data, nc]);

  // Proyectos presentes (prefijo del código: VB-, VI-…) para los chips de filtro.
  const proyectos = useMemo(() => {
    const set = new Set<string>();
    for (const o of data?.obras ?? []) {
      const p = o.codigo.split('-')[0];
      if (p) set.add(p);
    }
    return [...set].sort();
  }, [data]);

  // Lista filtrada por proyecto + búsqueda + categoría (sobre el preview).
  const obrasFiltradas = useMemo(() => {
    let lista = data?.obras ?? [];
    if (proyecto) lista = lista.filter((o) => o.codigo.startsWith(`${proyecto}-`));
    if (estadoFiltro) lista = lista.filter((o) => categoriaObra(o) === estadoFiltro);
    const q = busqueda.trim().toLowerCase();
    if (q) lista = lista.filter((o) => `${o.codigo} ${o.tipo_casa}`.toLowerCase().includes(q));
    return lista;
  }, [data, proyecto, busqueda, estadoFiltro]);
  const fijar = useFijarLineaBase();
  const revertirAvance = useRevertirAvance();
  const revertirPreview = useRevertirAvancePreview(Number(semana.id), revertirAbierto);
  const actualizarSemana = useActualizarSemana();
  const fotoB = useFotoB();

  async function handleFotoB() {
    try {
      const r = await fotoB.mutateAsync(fechaFotoB);
      toast.success(
        `Foto B (${fechaFotoB}) — ${r.data?.snapshots_creados ?? 0} snapshots de ${
          r.data?.obras ?? 0
        } obras.`,
      );
      setFotoBAbierto(false);
    } catch (e) {
      toast.error('No se pudo tomar la Foto B', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }
  // ¿Hay alguna obra con sprint_plan ya fijado en plan_semanal de esta semana?
  const lineaBaseFijada = (data?.obras ?? []).some((o) => o.sprint_plan != null);

  async function handleFijar() {
    try {
      const r = await fijar.mutateAsync(Number(semana.id));
      const d = r.data;
      if (d) {
        toast.success(`Línea base fijada · ${d.fijadas}/${d.total_obras} obras`);
      }
    } catch (e) {
      const esAvanceSuelto =
        e instanceof ErrorHttp && e.status === 409 && lineaBaseFijada && esAdminEstricto;
      toast.error('No se pudo fijar la línea base', {
        description: e instanceof Error ? e.message : 'Error desconocido',
        action: esAvanceSuelto
          ? {
              label: 'Revertir…',
              onClick: () => setRevertirAbierto(true),
            }
          : undefined,
      });
    }
  }

  /**
   * Revierte al último cierre el avance de las obras que están bloqueando el
   * re-fijado (409 de handleFijar) y reintenta fijar la línea base. Así no
   * hace falta digitar manualmente el avance del cierre anterior solo para
   * poder agregar una obra a la programación y re-fijar.
   */
  async function handleRevertirYRefijar() {
    try {
      const r = await revertirAvance.mutateAsync(Number(semana.id));
      const d = r.data;
      if (!d || d.obras_revertidas.length === 0) {
        toast.info('No había avance suelto para revertir.');
        return;
      }
      toast.success(
        `Avance revertido al último cierre en ${d.obras_revertidas.length} obra(s): ${d.obras_revertidas.join(', ')}. Volvé a digitar el avance de esta semana cuando corresponda.`,
      );
      await handleFijar();
    } catch (e) {
      toast.error('No se pudo revertir el avance', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  async function handleDias(valor: number) {
    if (valor === semana.dias_efectivos) return;
    try {
      await actualizarSemana.mutateAsync({
        semanaId: Number(semana.id),
        body: { dias_efectivos: valor },
      });
      toast.success(`Días efectivos: ${valor}`);
    } catch (e) {
      toast.error('No se pudo actualizar', {
        description: e instanceof Error ? e.message : 'Error',
      });
    }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-2 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Semana abierta</p>
            <p className="text-xl font-semibold">
              {semana.anio} · Semana {semana.numero_semana}
            </p>
            <p className="text-xs text-muted-foreground">
              {semana.fecha_inicio.slice(0, 10)} → {semana.fecha_fin.slice(0, 10)}
            </p>
          </div>
          {esAdmin && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <span>Días efectivos</span>
                <Input
                  type="number"
                  min={1}
                  max={7}
                  defaultValue={semana.dias_efectivos}
                  key={semana.dias_efectivos}
                  onBlur={(e) => handleDias(Number(e.target.value))}
                  className="h-8 w-14 text-center"
                  aria-label="Días efectivos"
                />
              </div>
              <Button
                variant={lineaBaseFijada ? 'outline' : 'default'}
                size="sm"
                onClick={handleFijar}
                disabled={fijar.isPending}
                className={lineaBaseFijada ? '' : 'bg-amber-500 text-white hover:bg-amber-600'}
              >
                {fijar.isPending ? (
                  <CircleNotch
                    className="mr-1 h-4 w-4 animate-spin"
                    weight="bold"
                    aria-hidden="true"
                  />
                ) : (
                  <Flag className="mr-1 h-4 w-4" weight="bold" aria-hidden="true" />
                )}
                {lineaBaseFijada ? 'Re-fijar línea base' : 'Fijar línea base'}
              </Button>
              {lineaBaseFijada && esAdminEstricto && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRevertirAbierto(true)}
                  disabled={revertirAvance.isPending}
                  title="Revierte el avance de las obras que están bloqueando el re-fijado al valor de su último cierre"
                >
                  {revertirAvance.isPending ? (
                    <CircleNotch
                      className="mr-1 h-4 w-4 animate-spin"
                      weight="bold"
                      aria-hidden="true"
                    />
                  ) : (
                    <ClockCounterClockwise
                      className="mr-1 h-4 w-4"
                      weight="bold"
                      aria-hidden="true"
                    />
                  )}
                  Revertir avance a cierre anterior
                </Button>
              )}
            </div>
          )}
        </div>
        {lineaBaseFijada && (
          <p className="text-[11px] text-amber-700">
            Línea base fijada — las obras adelantadas se marcan en ámbar en el Kanban.
          </p>
        )}
      </Card>

      {/* Bloque fijo (sticky): resumen + filtros + título, visible al hacer scroll */}
      <div className="sticky top-12 z-30 -mx-4 space-y-2 border-b border-border bg-zinc-50/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-zinc-50/80">
        {data && (
          <Card className="p-3">
            <div className="grid grid-cols-3 gap-2 text-center md:grid-cols-5">
              <Stat label="Avanzarán" value={data.totales.avanzaran} accent="lime" />
              <Stat
                label="Avanzó esta semana"
                value={data.totales.avanzaron_semana}
                accent="indigo"
              />
              <Stat label="Finalizarán" value={data.totales.finalizaran} accent="sky" />
              <Stat label="No completadas" value={data.totales.bloqueadas} accent="red" />
              <Stat label="Sin críticas" value={data.totales.sin_criticas} />
            </div>
          </Card>
        )}

        {(data?.obras ?? []).length > 0 && (
          <>
            {/* Proyecto */}
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => setProyecto(null)}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                  proyecto === null
                    ? 'border-adelante-primary bg-adelante-primary text-adelante-foreground'
                    : 'border-border bg-white text-muted-foreground',
                )}
              >
                Todos
              </button>
              {proyectos.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setProyecto(proyecto === p ? null : p)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                    proyecto === p
                      ? 'border-adelante-primary bg-adelante-primary text-adelante-foreground'
                      : 'border-border bg-white text-muted-foreground',
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
            {/* Estado / categoría */}
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => setEstadoFiltro(null)}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                  estadoFiltro === null
                    ? 'border-adelante-primary bg-adelante-primary text-adelante-foreground'
                    : 'border-border bg-white text-muted-foreground',
                )}
              >
                Todas
              </button>
              {(
                [
                  'avanzaran',
                  'avanzo_semana',
                  'finalizaran',
                  'no_completada',
                  'sin_criticas',
                ] as const
              ).map((c) => {
                const m = CATEGORIA_META[c];
                const activo = estadoFiltro === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setEstadoFiltro(activo ? null : c)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      activo ? m.chip : 'border-border bg-white text-muted-foreground',
                    )}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            {/* Buscador */}
            <div className="relative">
              <MagnifyingGlass
                className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                weight="bold"
                aria-hidden="true"
              />
              <Input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar obra…"
                className="pl-8"
              />
            </div>
          </>
        )}

        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Obras en ejecución
          {obrasFiltradas.length !== (data?.obras ?? []).length && (
            <span className="ml-1 normal-case text-muted-foreground">
              ({obrasFiltradas.length} de {(data?.obras ?? []).length})
            </span>
          )}
        </div>
      </div>

      <Card className="overflow-hidden">
        <ul className="divide-y divide-border">
          {obrasFiltradas.map((o) => (
            <ObraFila key={o.codigo} obra={o} />
          ))}
          {obrasFiltradas.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">
              {(data?.obras ?? []).length === 0
                ? 'No hay obras en ejecución.'
                : 'Ninguna obra coincide con el filtro.'}
            </li>
          )}
        </ul>
      </Card>

      {/* NC requeridas para el Corte A */}
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setFotoBAbierto(true)}>
            <Camera className="mr-1 h-4 w-4" weight="bold" aria-hidden="true" />
            Foto B
          </Button>
          <Button variant="outline" onClick={() => setModalNC(true)}>
            <XCircle className="mr-1 h-4 w-4" weight="bold" aria-hidden="true" />
            Asignar NC{pendientesSinNC > 0 ? ` (${pendientesSinNC})` : ''}
          </Button>
          <Button
            onClick={() => (pendientesSinNC > 0 ? setModalNC(true) : setConfirmar(true))}
            className="flex-1"
            size="lg"
          >
            <LockSimple className="mr-2 h-4 w-4" weight="bold" aria-hidden="true" />
            Cerrar semana
          </Button>
        </div>
        {pendientesSinNC > 0 && (
          <p className="text-xs text-amber-700">
            Faltan {pendientesSinNC} subpartida{pendientesSinNC === 1 ? '' : 's'} sin causa de NC.
            Asignalas para poder cerrar.
          </p>
        )}
      </div>

      <Dialog open={confirmar} onOpenChange={(o) => !o && setConfirmar(false)}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LockSimple className="h-5 w-5" weight="bold" aria-hidden="true" />
              Cerrar semana
            </DialogTitle>
            <DialogDescription>
              Se guardan snapshots inmutables del avance de cada obra; las que tengan TODAS sus
              críticas al 100% avanzan al siguiente sprint y las entregadas pasan a Terminadas. Las
              pendientes quedan con la causa de NC que asignaste. No se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="fecha-cortea" className="mb-1 block text-xs">
              Fecha de corte (debe calzar con planilla/gastos)
            </Label>
            <Input
              id="fecha-cortea"
              type="date"
              value={fechaCorteA}
              onChange={(e) => setFechaCorteA(e.target.value)}
              disabled={!corteEditable}
              className={corteEditable ? undefined : 'bg-muted text-muted-foreground'}
            />
            <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={corteEditable}
                onChange={(e) => {
                  setCorteEditable(e.target.checked);
                  // Al descongelar/congelar de nuevo, restaurar el miércoles de cierre.
                  if (!e.target.checked) setFechaCorteA(semana.fecha_fin.slice(0, 10));
                }}
                className="h-4 w-4 accent-adelante-primary"
              />
              Cambiar fecha de corte (solo si gerencia lo autoriza — por defecto: miércoles de
              cierre)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmar(false)} disabled={enviando}>
              Cancelar
            </Button>
            <Button onClick={() => onCerrar(nc, fechaCorteA)} disabled={enviando}>
              {enviando ? (
                <>
                  <CircleNotch
                    className="mr-2 h-4 w-4 animate-spin"
                    weight="bold"
                    aria-hidden="true"
                  />
                  Cerrando…
                </>
              ) : (
                'Sí, cerrar la semana'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={revertirAbierto} onOpenChange={(o) => !o && setRevertirAbierto(false)}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockCounterClockwise className="h-5 w-5" weight="bold" aria-hidden="true" />
              Revertir avance a cierre anterior
            </DialogTitle>
            <DialogDescription>
              En las obras listadas abajo, cada sub-partida vuelve al % de su último cierre (Corte A
              o Foto B) — si nunca se cerró, vuelve a 0%. El avance digitado esta semana en esas
              obras se pierde y hay que volver a digitarlo. Después se reintenta fijar la línea base
              automáticamente. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            {revertirPreview.isLoading ? (
              <span className="flex items-center gap-2 text-muted-foreground">
                <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
                Buscando obras con avance suelto…
              </span>
            ) : revertirPreview.isError ? (
              <span className="text-red-600">No se pudo consultar qué obras se afectarían.</span>
            ) : (revertirPreview.data?.data?.obras.length ?? 0) === 0 ? (
              <span className="text-muted-foreground">
                No hay avance suelto ahora mismo — no hay nada que revertir.
              </span>
            ) : (
              <>
                <p className="mb-1 font-medium">
                  Se revertirán {revertirPreview.data?.data?.obras.length} obra(s):
                </p>
                <p className="font-mono text-xs">{revertirPreview.data?.data?.obras.join(', ')}</p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRevertirAbierto(false)}
              disabled={revertirAvance.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                await handleRevertirYRefijar();
                setRevertirAbierto(false);
              }}
              disabled={
                revertirAvance.isPending ||
                revertirPreview.isLoading ||
                (revertirPreview.data?.data?.obras.length ?? 0) === 0
              }
            >
              {revertirAvance.isPending ? (
                <>
                  <CircleNotch
                    className="mr-2 h-4 w-4 animate-spin"
                    weight="bold"
                    aria-hidden="true"
                  />
                  Revirtiendo…
                </>
              ) : (
                'Sí, estoy seguro — revertir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AsignarNCCierreDialog
        abierto={modalNC}
        onClose={() => setModalNC(false)}
        obras={data?.obras ?? []}
        ncMap={nc}
        onGuardar={setNc}
      />

      <Dialog open={fotoBAbierto} onOpenChange={(o) => !o && setFotoBAbierto(false)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" weight="bold" aria-hidden="true" />
              Foto B
            </DialogTitle>
            <DialogDescription>
              Snapshot del avance actual, sin cerrar la semana ni pedir NC. Elegí la fecha de corte
              que etiqueta la foto.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="fecha-fotob" className="mb-1 block text-xs">
              Fecha de corte
            </Label>
            <Input
              id="fecha-fotob"
              type="date"
              value={fechaFotoB}
              onChange={(e) => setFechaFotoB(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFotoBAbierto(false)}
              disabled={fotoB.isPending}
            >
              Cancelar
            </Button>
            <Button onClick={handleFotoB} disabled={fotoB.isPending}>
              {fotoB.isPending ? (
                <>
                  <CircleNotch
                    className="mr-2 h-4 w-4 animate-spin"
                    weight="bold"
                    aria-hidden="true"
                  />
                  Tomando…
                </>
              ) : (
                'Tomar Foto B'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: { label: string; value: number; accent?: 'lime' | 'red' | 'sky' | 'indigo' }) {
  return (
    <div>
      <p
        className={cn(
          'text-2xl font-semibold tabular-nums',
          accent === 'lime' && 'text-adelante-primary-dark',
          accent === 'red' && 'text-red-600',
          accent === 'sky' && 'text-sky-600',
          accent === 'indigo' && 'text-indigo-600',
        )}
      >
        {value}
      </p>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

function ObraFila({ obra }: { obra: PreviewObra }) {
  const [abierto, setAbierto] = useState(false);
  // Si avanzó esta semana, lo "pendiente" del sprint planificado son las
  // arrastradas (lo que quedó), no las del sprint avanzado (no vencido).
  const pendientes = (obra.avanzo_semana ? obra.arrastradas : obra.subs).filter(
    (s) => !s.completada,
  );

  return (
    <li>
      <button
        type="button"
        onClick={() => setAbierto((x) => !x)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-50"
      >
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-mono font-semibold">
            {obra.codigo}
            <Badge variant="outline" className="font-mono text-[10px]">
              {obra.tipo_casa}
            </Badge>
            <span className="text-xs font-normal text-muted-foreground">
              Sprint {obra.sprint_actual}
            </span>
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Críticas {obra.criticas_completas}/{obra.criticas_total} · pendientes{' '}
            {obra.pendientes_no_completadas}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {obra.va_a_avanzar && obra.sprint_siguiente !== null ? (
            <Badge className="bg-adelante-primary text-adelante-foreground">
              <ArrowCircleRight className="mr-1 h-3 w-3" weight="bold" aria-hidden="true" />S
              {obra.sprint_actual} → S{obra.sprint_siguiente}
            </Badge>
          ) : obra.va_a_avanzar ? (
            <Badge className="bg-sky-500 text-white hover:bg-sky-600">
              <CheckCircle className="mr-1 h-3 w-3" weight="bold" aria-hidden="true" />
              Finalizará
            </Badge>
          ) : obra.avanzo_semana ? (
            <Badge className="bg-indigo-500 text-white hover:bg-indigo-600">
              <ArrowCircleRight className="mr-1 h-3 w-3" weight="bold" aria-hidden="true" />
              Avanzó esta semana
            </Badge>
          ) : obra.criticas_total === 0 ? (
            <Badge variant="secondary">Sin críticas</Badge>
          ) : (
            <Badge variant="destructive">No completada</Badge>
          )}
        </div>
      </button>
      {abierto && pendientes.length > 0 && (
        <ul className="space-y-1 border-t border-border bg-zinc-50/50 px-4 py-2 text-xs">
          {pendientes.map((s) => (
            <li key={s.sub_partida_id} className="flex items-center gap-2">
              <span className="font-mono text-muted-foreground">{s.codigo}</span>
              <span className="truncate">{s.nombre}</span>
              {s.es_critica && (
                <span className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700">
                  crítica
                </span>
              )}
              <span className="ml-auto tabular-nums text-muted-foreground">
                {Math.round(s.pct_completado)}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

// ===========================================================================
// Resultado del cierre.
// ===========================================================================

function ResultadoVista({
  resultado,
  esAdmin,
  onSeguir,
}: {
  resultado: ResultadoCorteA;
  esAdmin: boolean;
  onSeguir: () => void;
}) {
  const [abrirSig, setAbrirSig] = useState(false);

  return (
    <div className="space-y-4">
      <Card className="space-y-3 p-5">
        <div className="flex items-center gap-2 text-adelante-primary-dark">
          <CheckCircle className="h-7 w-7" weight="fill" aria-hidden="true" />
          <h2 className="text-lg font-semibold">Semana cerrada</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
          <Detalle label="Obras procesadas" value={resultado.obras_procesadas} />
          <Detalle label="Avanzaron" value={resultado.obras_avanzaron.length} />
          <Detalle label="Avanzó semana" value={resultado.obras_avanzaron_semana.length} />
          <Detalle label="Finalizaron" value={resultado.obras_finalizaron.length} />
          <Detalle label="No completadas" value={resultado.obras_bloqueadas.length} />
          <Detalle label="Snapshots" value={resultado.snapshots_creados} />
        </div>
        {resultado.obras_avanzaron.length > 0 && (
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Avanzaron de sprint
            </p>
            <ul className="space-y-0.5 text-xs">
              {resultado.obras_avanzaron.map((o) => (
                <li key={o.codigo} className="font-mono">
                  {o.codigo}: S{o.sprint_de} → S{o.sprint_a}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {esAdmin && !abrirSig && (
        <Button onClick={() => setAbrirSig(true)} className="w-full">
          <CalendarPlus className="mr-2 h-4 w-4" weight="bold" aria-hidden="true" />
          Abrir siguiente semana
        </Button>
      )}
      {abrirSig && <AbrirSemanaForm />}

      <Button variant="outline" onClick={onSeguir} className="w-full">
        Volver
      </Button>
    </div>
  );
}

function Detalle({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border bg-white px-3 py-2">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
}
